import React, { useState } from 'react';
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Typography,
  Box,
  Chip,
  TextField,
  InputAdornment,
  Alert,
  Tooltip
} from '@mui/material';
import { Search as SearchIcon, Info as InfoIcon } from '@mui/icons-material';

interface DataTableProps {
  title: string;
  data: any[];
  columns: {
    id: string;
    label: string;
    format?: (value: any) => React.ReactNode;
    width?: string;
  }[];
  metadata?: {
    unique_records?: number;
    duplicate_count?: number;
    total_raw_records?: number;
    deduplication_info?: {
      method: string;
      description: string;
    };
  };
}

const DataTable: React.FC<DataTableProps> = ({ title, data, columns, metadata }) => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(+event.target.value);
    setPage(0);
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
    setPage(0);
  };

  // Filter data based on search term
  const filteredData = data.filter(row => {
    if (!searchTerm) return true;
    
    // Search across all fields
    return Object.values(row).some(value => 
      value !== null && 
      value !== undefined && 
      value.toString().toLowerCase().includes(searchTerm.toLowerCase())
    );
  });

  // Get the data for the current page
  const currentPageData = filteredData.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  return (
    <Paper sx={{ width: '100%', overflow: 'hidden', mb: 4 }}>
      <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="h6" component="div">
            {title}
          </Typography>
          <Chip label={`${filteredData.length} records`} size="small" />
          {metadata?.duplicate_count && metadata.duplicate_count > 0 && (
            <Tooltip title={metadata.deduplication_info?.description || 'Duplicates detected'}>
              <Chip 
                label={`${metadata.duplicate_count} duplicates`} 
                size="small" 
                color="warning"
                icon={<InfoIcon />}
              />
            </Tooltip>
          )}
          {metadata?.unique_records && (
            <Chip 
              label={`${metadata.unique_records} unique`} 
              size="small" 
              color="success"
            />
          )}
        </Box>
        <TextField
          variant="outlined"
          size="small"
          placeholder="Search..."
          value={searchTerm}
          onChange={handleSearchChange}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
      </Box>
      {metadata?.deduplication_info && metadata.duplicate_count && metadata.duplicate_count > 0 && (
        <Box sx={{ px: 2, pb: 2 }}>
          <Alert severity="info" sx={{ fontSize: '0.875rem' }}>
            <strong>Data Quality Note:</strong> {metadata.deduplication_info.description}
          </Alert>
        </Box>
      )}
      <TableContainer sx={{ maxHeight: 440 }}>
        <Table stickyHeader aria-label={`${title} table`}>
          <TableHead>
            <TableRow>
              {columns.map((column) => (
                <TableCell
                  key={column.id}
                  style={{ width: column.width }}
                >
                  <Typography variant="subtitle2" fontWeight="bold">
                    {column.label}
                  </Typography>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {currentPageData.map((row, index) => (
              <TableRow hover tabIndex={-1} key={index}>
                {columns.map((column) => {
                  const value = row[column.id];
                  return (
                    <TableCell key={column.id}>
                      {column.format ? column.format(value) : value}
                    </TableCell>
                  );
                })}
              </TableRow>
            ))}
            {currentPageData.length === 0 && (
              <TableRow>
                <TableCell colSpan={columns.length} align="center">
                  {searchTerm ? 'No matching records found' : 'No data available'}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        rowsPerPageOptions={[10, 25, 50, 100]}
        component="div"
        count={filteredData.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />
    </Paper>
  );
};

export default DataTable;
